export const signupPlans = ["lite", "standard", "pro", "master"] as const;
export type SignupPlan = (typeof signupPlans)[number];
export type SignupInterval = "monthly" | "yearly";
export interface SignupSelection {
  plan: SignupPlan;
  interval: SignupInterval;
}

export interface SignupAttribution {
  utm_source?: string;
  promo_code?: string;
}
export type SignupIntent = SignupSelection & SignupAttribution;

// These are marketing hints, never account identity, prices or entitlements.
// Match Stripe Payment Link's supported parameter syntax.
export function signupAttribution(value?: SignupAttribution): SignupAttribution {
  const result: SignupAttribution = {};
  if (typeof value?.utm_source === "string" && /^[a-zA-Z0-9_-]{1,150}$/.test(value.utm_source.trim())) {
    result.utm_source = value.utm_source.trim();
  }
  if (typeof value?.promo_code === "string" && /^[a-zA-Z0-9]{1,200}$/.test(value.promo_code.trim())) {
    result.promo_code = value.promo_code.trim();
  }
  return result;
}

// Bot signups pad Gmail addresses with dots (a.b.c.d.e@gmail.com). Real
// addresses rarely have more than three in the local part.
export function isBlockedSignupEmail(email: string): boolean {
  const local = email.trim().split("@")[0] ?? "";
  return (local.match(/\./g) ?? []).length >= 4;
}

export const blockedSignupEmailMessage =
  "We can't create an account with this email. Please contact support at contact@mountmonitor.com.";

export const signupPlanNames: Record<SignupPlan, string> = {
  lite: "Lite Mount",
  standard: "Standard Mount",
  pro: "Pro Mount",
  master: "Master Mount",
};

// Matuska hands its customers MATUSKA50. They come for the Matuska catalog,
// which Lite doesn't include, so signup offers Standard before checkout.
export function isMatuskaPromo(code?: string): boolean {
  return /^matuska/i.test(code ?? "");
}

export function isSignupPlan(value: unknown): value is SignupPlan {
  return signupPlans.some((plan) => plan === value);
}

export function isSignupInterval(value: unknown): value is SignupInterval {
  return value === "monthly" || value === "yearly";
}

export function savedSignupSelection(
  saved?: { plan?: unknown; interval?: unknown }
): SignupSelection | null {
  return isSignupPlan(saved?.plan) && isSignupInterval(saved?.interval)
    ? { plan: saved.plan, interval: saved.interval }
    : null;
}

export function parseSignupSelection(
  params: Pick<URLSearchParams, "get">
): SignupSelection | null {
  const plan = params.get("plan");
  const requestedInterval = params.get("interval") || "monthly";
  const interval = requestedInterval === "annual" || requestedInterval === "annually"
    ? "yearly" : requestedInterval;
  return isSignupPlan(plan) && isSignupInterval(interval)
    ? { plan, interval }
    : null;
}

export function parseSignupIntent(
  params: Pick<URLSearchParams, "get">
): SignupIntent | null {
  // Auth redirects sometimes wrap the original signup URL in returnUrl.
  // Read only a local signup/login destination; never arbitrary external URLs.
  const returning = safeSignupReturnUrl(params.get("returnUrl"));
  const returnUrl = returning ? new URL(returning, "https://app.mountmonitor.com") : null;
  const nested = returnUrl && ["/signup", "/login"].includes(returnUrl.pathname)
    ? returnUrl.searchParams : null;
  const source = params.get("plan") ? params : nested ?? params;
  const selection = parseSignupSelection(source);
  if (!selection) return null;
  return {
    ...selection,
    ...signupAttribution({
      utm_source: source.get("utm_source") ?? undefined,
      promo_code: source.get("promo_code") ?? source.get("promo") ?? source.get("prefilled_promo_code") ?? undefined,
    }),
  };
}

export function savedSignupIntent(
  saved?: Partial<SignupIntent>
): SignupIntent | null {
  const selection = savedSignupSelection(saved);
  return selection ? { ...selection, ...signupAttribution(saved) } : null;
}

// Only paths inside this app may be used after signing in.
export function safeSignupReturnUrl(value: string | null): string | null {
  if (!value || !value.startsWith("/") || value.startsWith("//")) return null;
  try {
    const origin = "https://app.mountmonitor.com";
    const url = new URL(value, origin);
    return url.origin === origin ? url.pathname + url.search + url.hash : null;
  } catch {
    return null;
  }
}

// Pending accounts land on their saved-plan screen. The account-creation
// action separately requests a one-time automatic handoff; URL flags and
// later logins cannot start checkout automatically.
export function pendingSignupUrl(
  params: Pick<URLSearchParams, "get">,
  saved?: Partial<SignupIntent>
): string {
  const returning = safeSignupReturnUrl(params.get("returnUrl"));
  if (returning?.startsWith("/signup/complete?")) return returning;

  // An old browser link must not replace the plan they last checked out with.
  const selection = savedSignupIntent(saved) ?? parseSignupIntent(params);
  if (!selection) return "/signup";
  const query = new URLSearchParams({ ...selection });
  return `/signup?${query}`;
}
